/**
 * Regras do acesso master no seletor de conta do cabecalho. Sem imports de
 * proposito: roda no `node --test`, fora do webpack.
 */

export const MIN_SEARCH_LENGTH = 2
export const SEARCH_DEBOUNCE_MS = 300

/**
 * Resultado de busca master pode vir sem id (a conta de acesso admin nasce na
 * primeira entrada), entao a chave cai para a empresa-alvo.
 */
export function accountKey(account) {
  return account.id === null && account.master_target
    ? `m:${account.master_target.type}:${account.master_target.id}`
    : `u:${account.id}`
}

/** Conta master vai por master_target: o kyojin revalida empresa e flag a cada entrada. */
export function switchPayload(account) {
  if (account.is_master_access && account.master_target) {
    return { master_target: account.master_target }
  }

  return { user_id: account.id }
}

/**
 * Para onde a sessao nova vai. Cliente fica aqui (routes.js adota o ?token=);
 * fornecedor e admin saem para o app deles. Sem URL configurada devolve null —
 * quem chama avisa, em vez de mandar para um link quebrado.
 */
export function redirectUrlFor(type, token, { providerUrl, adminUrl } = {}) {
  const encoded = encodeURIComponent(token)

  if (type === 'client') {
    return `/?token=${encoded}`
  }

  const base = type === 'provider' ? providerUrl : type === 'admin' ? adminUrl : null

  if (!base) {
    return null
  }

  const clean = base.replace(/\/+$/, '')

  return type === 'provider' ? `${clean}/login?token=${encoded}` : `${clean}/?token=${encoded}`
}

/**
 * So o cliente fica neste app depois da troca; fornecedor e admin saem por
 * `redirectUrlFor` para um app diferente. Quem chama usa isso para decidir
 * se pode derrubar a sessao atual antes de navegar (ver switch_account no
 * Header): sair para outro app nao pode.
 */
export function isSameApp(type) {
  return type === 'client'
}

export const ACCOUNT_TYPE_LABELS = {
  admin: 'Administração',
  client: 'Cliente',
  provider: 'Fornecedor',
}

/** Mesma sequencia do seletor dos apps ymir: cada linha ganha uma cor pelo indice. */
const AVATAR_COLORS = ['#ff5722', '#283593', '#388e3c', '#c2185b', '#00796b']

export function avatarColor(index) {
  return AVATAR_COLORS[index % AVATAR_COLORS.length]
}

export function initialsOf(label) {
  const words = String(label || '').trim().split(/\s+/).filter(Boolean)
  return ((words[0] ? words[0][0] : '') + (words[1] ? words[1][0] : '')).toUpperCase()
}

/** CNPJ so com os 14 digitos; qualquer outra coisa devolve null (a linha mostra so o papel). */
export function formatCnpj(document) {
  const digits = String(document || '').replace(/\D/g, '')
  if (digits.length !== 14) {
    return null
  }

  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

export function companyNameOf(account) {
  return account.client_name || account.provider_name || null
}

/**
 * Segunda linha da conta no seletor. Conta de acesso master nao tem usuario
 * proprio para mostrar: vai o CNPJ da empresa (so vem na busca) ou o papel.
 */
export function secondaryLabel(account, isCurrent) {
  const role = ACCOUNT_TYPE_LABELS[account.type] || ''

  if (account.is_master_access) {
    if (isCurrent) {
      return 'Conta atual · Acesso master'
    }
    const cnpj = formatCnpj(account.document)
    return cnpj ? `${role} · CNPJ ${cnpj}` : role
  }

  const roleText = isCurrent ? 'Conta atual' : role
  return companyNameOf(account) ? `${roleText} · ${account.name}` : roleText
}

/** Filtro local das contas do proprio login, por nome da empresa ou da conta. */
export function filterOwnAccounts(accounts, term) {
  const lowered = String(term || '').trim().toLowerCase()
  if (!lowered) {
    return accounts
  }

  return accounts.filter((account) =>
    [companyNameOf(account), account.name]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(lowered))
  )
}

/**
 * Nome no cabecalho. Dentro de uma conta de acesso master o usuario e o login
 * master, nao alguem da empresa: mostrar a empresa deixa claro onde ele esta.
 */
export function headerUserName(userName, accounts, currentAccountId) {
  const current = (accounts || []).find((account) => account.id !== null && account.id === currentAccountId)
  const company = current && current.is_master_access ? companyNameOf(current) : null

  return company ? `${company} · master` : userName
}
